import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails
} from 'amazon-cognito-identity-js'

function getPoolConfig(): { UserPoolId: string; ClientId: string } | null {
  const UserPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID?.trim() || ''
  const ClientId = process.env.REACT_APP_COGNITO_CLIENT_ID?.trim() || ''
  if (!UserPoolId || !ClientId) {
    if (process.env.NODE_ENV !== 'test') {
      console.warn(
        'Cognito env missing. Set REACT_APP_COGNITO_USER_POOL_ID and REACT_APP_COGNITO_CLIENT_ID in client/.env, then restart the frontend dev server.'
      )
    }
    return null
  }
  return { UserPoolId, ClientId }
}

let userPoolInstance: CognitoUserPool | null = null

function getUserPool(): CognitoUserPool | null {
  if (userPoolInstance) return userPoolInstance
  const config = getPoolConfig()
  if (!config) return null
  userPoolInstance = new CognitoUserPool(config)
  return userPoolInstance
}

export function getCurrentUser(): CognitoUser | null {
  const pool = getUserPool()
  return pool ? pool.getCurrentUser() : null
}

export function getUserAttributes(
  user: CognitoUser
): Promise<Record<string, string>> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => reject(new Error('Timeout getting user attributes')), 10000)
    user.getSession((err: Error | null, session: any) => {
      if (err) {
        clearTimeout(timeout)
        reject(err)
        return
      }
      if (!session?.isValid?.()) {
        clearTimeout(timeout)
        reject(new Error('Invalid session'))
        return
      }
      user.getUserAttributes((attrErr, attributes) => {
        clearTimeout(timeout)
        if (attrErr) {
          reject(attrErr)
          return
        }
        const out: Record<string, string> = {}
        attributes?.forEach((attr) => {
          out[attr.getName()] = attr.getValue()
        })
        resolve(out)
      })
    })
  })
}

export function getJwtToken(): Promise<string | null> {
  return new Promise((resolve) => {
    const user = getCurrentUser()
    if (!user) {
      resolve(null)
      return
    }
    const timeout = setTimeout(() => resolve(null), 10000)
    user.getSession((err: Error | null, session: any) => {
      clearTimeout(timeout)
      if (err || !session?.isValid?.()) {
        resolve(null)
        return
      }
      resolve(session.getIdToken().getJwtToken())
    })
  })
}

export function signIn(
  username: string,
  password: string
): Promise<{ user: CognitoUser; attributes: Record<string, string> }> {
  const pool = getUserPool()
  if (!pool) {
    return Promise.reject(
      new Error('Cognito not configured. Set REACT_APP_COGNITO_USER_POOL_ID and REACT_APP_COGNITO_CLIENT_ID in client/.env')
    )
  }
  return new Promise((resolve, reject) => {
    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: password
    })
    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: pool
    })
    cognitoUser.authenticateUser(authDetails, {
      onSuccess: async () => {
        try {
          const attributes = await getUserAttributes(cognitoUser)
          resolve({ user: cognitoUser, attributes })
        } catch (e) {
          reject(e)
        }
      },
      onFailure: (err) => reject(err),
      newPasswordRequired: () => {
        reject(new Error('New password required. Please contact administrator.'))
      }
    })
  })
}

export function signOut(): void {
  const user = getCurrentUser()
  if (user) user.signOut()
}

export function isAuthenticated(): Promise<boolean> {
  return new Promise((resolve) => {
    if (!getUserPool()) {
      resolve(false)
      return
    }
    const user = getCurrentUser()
    if (!user) {
      resolve(false)
      return
    }
    const timeout = setTimeout(() => resolve(false), 5000)
    user.getSession((err: Error | null, session: any) => {
      clearTimeout(timeout)
      resolve(!err && session?.isValid?.() === true)
    })
  })
}
